import { ACTION_BASE_EVENTS, ACTION_RESULTS, CONTACT_FIELDS, FIELD_VALUE_TYPES, FORM_RESULTS, NEGATIVE_OPERATORS, OPERATORS_BY_VALUE_TYPE, QUESTION_RESULTS, RELATIONSHIP_ACCESS_LEVELS, RUNTIME_EVENTS, SCRATCHPAD_EVENT_DESCRIPTIONS, SCRATCHPAD_EVENT_LABELS, SYSTEM_FIELDS, TRANSFER_NODE_EVENTS, actionResult, choicesOfAnswer, equationRowKey, nodesBefore, valueTypeOfAnswer, variableName, } from '@super/shared-types';
import { END_NODE_ID, TRIGGER_NODE_ID, eventDescription, eventLabel, eventsFrom, isFlowStartNodeId, } from './schema';
const QUESTION_STATE_LABELS = {
    conforms: 'Received expected answer',
    off_type: 'Received unexpected answer',
    unclear: 'Received unclear answer',
    skipped: 'Already known, not asked',
};
const ACTION_EVENTS = new Set(ACTION_BASE_EVENTS);
const ACTION_STATE_LABELS = {
    completed: 'Completed',
    failed: 'Failed',
};
// The group fails when one action fails, so its states read as a count.
const GROUP_STATE_LABELS = {
    completed: 'Completed',
    failed: 'Any failed',
};
const GROUP_ROW_LABELS = {
    completed: 'All actions completed',
    failed: 'An action failed',
};
const FORM_STATE_LABELS = {
    completed: 'Form completed',
    required_complete: 'Required questions complete',
};
export const OPERATOR_LABELS = {
    equals: 'Equals (=)',
    not_equals: 'Does not equal (≠)',
    greater_than: 'Greater than (>)',
    less_than: 'Less than (<)',
    greater_or_equal: 'Greater than or equal to (≥)',
    less_or_equal: 'Less than or equal to (≤)',
    contains: 'Contains (∋)',
    not_contains: 'Does not contain (∌)',
    exists: 'Exists (∃)',
    not_exists: 'Does not exist (∄)',
};
/** What an operator shows once picked, in the arrow panel. */
export const OPERATOR_SYMBOLS = {
    equals: '=',
    not_equals: '≠',
    greater_than: '>',
    less_than: '<',
    greater_or_equal: '≥',
    less_or_equal: '≤',
    contains: '∋',
    not_contains: '∌',
    exists: '∃',
    not_exists: '∄',
};
const OPERATOR_SIGNS = {
    equals: '=',
    not_equals: '≠',
    greater_than: '>',
    less_than: '<',
    greater_or_equal: '≥',
    less_or_equal: '≤',
    contains: 'contains',
    not_contains: 'does not contain',
    exists: 'exists',
    not_exists: 'does not exist',
};
/** The canvas as the contract's route map: Start and flow pills leave no node. */
export function routeMapOf(state) {
    return {
        nodes: state.steps.map(step => ({ id: step.id })),
        edges: state.arrows.map(arrow => ({
            fromNodeId: arrow.source === TRIGGER_NODE_ID || isFlowStartNodeId(arrow.source)
                ? null
                : arrow.source,
            toNodeId: arrow.target === END_NODE_ID ? null : arrow.target,
            condition: arrow.source === TRIGGER_NODE_ID
                ? { type: 'event', event: RUNTIME_EVENTS.SESSION_STARTED }
                : { type: 'equation', mode: 'all', rows: [] },
        })),
    };
}
/** The nodes a call can reach before it ends; an end node's arrows run after the call. */
export function reachedDuringCall(state) {
    const ending = new Set(state.steps
        .filter(step => step.type === 'end_conversation')
        .map(step => step.id));
    const reached = new Set();
    const stack = state.arrows
        .filter(a => a.source === TRIGGER_NODE_ID || isFlowStartNodeId(a.source))
        .map(a => a.target);
    while (stack.length > 0) {
        const id = stack.pop();
        if (reached.has(id))
            continue;
        reached.add(id);
        if (ending.has(id))
            continue;
        for (const a of state.arrows)
            if (a.source === id)
                stack.push(a.target);
    }
    return reached;
}
/** Everything the first dropdown offers for an arrow, in display order. */
export function entriesFor(state, arrow, routing, actions = {}) {
    const reach = nodesBefore(routeMapOf(state), { fromNodeId: arrow.source });
    const earlier = state.steps.filter(step => reach.has(step.id) && step.id !== arrow.source);
    const entries = [];
    for (const field of SYSTEM_FIELDS) {
        entries.push(variableEntry('System variables', { source: 'system', field }, trueFalse()));
    }
    for (const field of CONTACT_FIELDS) {
        const ref = { source: 'contact', field };
        if (field === 'property' || field === 'property_group') {
            const { choices, note } = routingChoices(routing, field);
            entries.push(variableEntry('Contact variables', ref, choices, { emptyNote: note }));
        }
        else {
            const choices = field === 'group'
                ? RELATIONSHIP_ACCESS_LEVELS.map(value => ({
                    value,
                    label: value.toLowerCase(),
                }))
                : [];
            entries.push(variableEntry('Contact variables', ref, choices));
        }
    }
    // Answers of earlier nodes, read from anywhere in the call.
    for (const step of earlier) {
        for (const item of answerItems(step)) {
            entries.push({
                ...variableEntry('Custom variables', { source: item.source, nodeId: step.id, item: item.id }, item.choices, {
                    maybeMissing: reach.get(step.id) === 'some',
                    type: item.type,
                }),
                preview: item.text,
            });
        }
    }
    // Events the graph listens for count from anywhere; an earlier node's
    // events count when that node raised them.
    for (const name of state.watchEvents) {
        entries.push(eventEntry(`event::${name}`, SCRATCHPAD_EVENT_LABELS[name], { kind: 'event', event: name }, 'Events', SCRATCHPAD_EVENT_DESCRIPTIONS[name]));
    }
    for (const step of earlier) {
        for (const name of nodeEvents(step)) {
            entries.push(eventEntry(`event:${step.id}:${name}`, `${step.title || 'Untitled node'}: ${eventLabel(name)}`, { kind: 'event', event: name, nodeId: step.id }, 'Events', eventDescription(name)));
        }
    }
    const source = state.steps.find(step => step.id === arrow.source);
    if (source)
        entries.push(...outputEntries(state, source, actions));
    // An action event the arrow already waits on, from before action nodes
    // offered their results.
    if (source?.type === 'action') {
        for (const row of arrow.condition?.rows ?? []) {
            if (row.kind === 'event' && ACTION_EVENTS.has(row.event)) {
                entries.push({
                    ...eventEntry(`event:own:${row.event}`, eventLabel(row.event), { kind: 'event', event: row.event }, 'Source node outputs'),
                    hidden: true,
                });
            }
        }
    }
    return entries;
}
/** The outputs of the node the arrow leaves. */
function outputEntries(state, step, actions) {
    const entries = [];
    const group = 'Source node outputs';
    if (step.type === 'action') {
        // Actions run only after the call, so during it nothing fails and no
        // single action reports.
        const afterCall = !reachedDuringCall(state).has(step.id);
        const states = (actionId) => (afterCall ? ACTION_RESULTS : ['completed']).map(is => ({
            key: is,
            label: (actionId === undefined
                ? GROUP_STATE_LABELS
                : ACTION_STATE_LABELS)[is],
            row: actionResult(step.id, is, actionId),
        }));
        entries.push({
            key: `action:${step.id}`,
            group,
            label: 'All actions',
            states: states(),
        });
        for (const action of afterCall
            ? (actions[step.nodeActionGroupId] ?? [])
            : []) {
            entries.push({
                key: `action:${step.id}:${action.value}`,
                group,
                label: action.label,
                states: states(action.value),
            });
        }
    }
    if (step.type === 'form') {
        for (const is of FORM_RESULTS) {
            entries.push(eventEntry(`form:${step.id}:${is}`, FORM_STATE_LABELS[is], { kind: 'result', source: 'form', nodeId: step.id, is }, group));
        }
    }
    for (const item of answerItems(step)) {
        const type = item.type;
        const states = item.source === 'question'
            ? QUESTION_RESULTS.map(is => ({
                key: is,
                label: QUESTION_STATE_LABELS[is],
                row: {
                    kind: 'result',
                    source: 'question',
                    nodeId: step.id,
                    item: item.id,
                    is,
                },
            }))
            : [];
        entries.push({
            key: `${item.source}:${step.id}:${item.id}`,
            group,
            label: `{${item.id}}`,
            preview: item.text,
            states,
            // The node's own signal is missing until the node hears it, so a check
            // that holds while it is missing is not offered. A form's own question
            // takes every check: a negative one counts once the form has finished.
            value: {
                ref: { source: item.source, nodeId: step.id, item: item.id },
                type,
                choices: item.choices,
                operators: item.source === 'signal'
                    ? OPERATORS_BY_VALUE_TYPE[type].filter(op => !NEGATIVE_OPERATORS.includes(op))
                    : OPERATORS_BY_VALUE_TYPE[type],
                maybeMissing: false,
            },
        });
    }
    if (step.type === 'transfer') {
        entries.push({
            key: `transfer:${step.id}`,
            group,
            label: 'Call connection',
            states: TRANSFER_NODE_EVENTS.map(event => ({
                key: event,
                label: eventLabel(event),
                row: { kind: 'event', event },
                description: eventDescription(event),
            })),
        });
    }
    for (const name of nodeEvents(step)) {
        entries.push(eventEntry(`event:own:${name}`, eventLabel(name), { kind: 'event', event: name }, group, eventDescription(name)));
    }
    return entries;
}
/** The entry and state a stored row stands for, or null when the canvas has neither. */
export function entryOf(row, entries) {
    const key = equationRowKey(row);
    for (const entry of entries) {
        const state = entry.states.find(s => equationRowKey(s.row) === key);
        if (state)
            return { entry, state };
    }
    if (row.kind !== 'value')
        return null;
    const ref = refKey(row.ref);
    const entry = entries.find(e => e.value && refKey(e.value.ref) === ref);
    return entry ? { entry } : null;
}
/** A new row for an entry: its first state, or its value. */
export function rowForEntry(entry) {
    if (entry.states.length > 0)
        return entry.states[0].row;
    if (!entry.value)
        return null;
    return pickedFromList(entry.value.type)
        ? {
            kind: 'value',
            ref: entry.value.ref,
            op: 'equals',
            value: firstValue(entry.value),
        }
        : { kind: 'value', ref: entry.value.ref, op: 'exists' };
}
/** Whether a value is picked from a list rather than typed. */
export function pickedFromList(type) {
    return type === 'choice' || type === 'id' || type === 'boolean';
}
/** A value picked from a list, as the row stores it. */
export function storedValue(type, picked) {
    return type === 'boolean' ? picked === 'true' : picked;
}
export function firstValue(value) {
    if (pickedFromList(value.type)) {
        return storedValue(value.type, value.choices[0]?.value ?? '');
    }
    if (value.type === 'integer')
        return 0;
    return '';
}
/**
 * A row as one line of words. `valueLabel` names an id or choice, and
 * `actionLabel` an action, when known.
 */
export function rowLabel(row, valueLabel = () => undefined, actionLabel = () => undefined) {
    if (row.kind === 'event')
        return eventLabel(row.event);
    if (row.kind === 'result') {
        if (row.source === 'action') {
            if (row.actionId === undefined)
                return GROUP_ROW_LABELS[row.is];
            return `${actionLabel(row.actionId) ?? 'An action'}: ${ACTION_STATE_LABELS[row.is].toLowerCase()}`;
        }
        if (row.source === 'form')
            return FORM_STATE_LABELS[row.is];
        return `{${row.item}}: ${QUESTION_STATE_LABELS[row.is].toLowerCase()}`;
    }
    const value = row.value === undefined ? '' : ` ${valueLabel(row) ?? String(row.value)}`;
    return `${variableName(row.ref)} ${OPERATOR_SIGNS[row.op]}${value}`;
}
/** What the arrow's transition row says. */
export function summarize(condition, valueLabel, actionLabel) {
    if (!condition || condition.rows.length === 0)
        return '';
    const first = rowLabel(condition.rows[0], valueLabel, actionLabel);
    const more = condition.rows.length - 1;
    if (more === 0)
        return first;
    return `${first} ${condition.mode === 'all' ? 'and' : 'or'} ${more} more`;
}
/** Names the role, property and group a row compares with, from the host's lists. */
export function valueLabelFrom(routing) {
    return (row) => {
        if (row.ref.source !== 'contact' || typeof row.value !== 'string') {
            return undefined;
        }
        if (row.ref.field === 'group')
            return row.value.toLowerCase();
        if (typeof routing !== 'object')
            return undefined;
        const list = row.ref.field === 'property'
            ? routing.properties
            : row.ref.field === 'property_group'
                ? routing.propertyGroups
                : [];
        return list.find(choice => choice.value === row.value)?.label;
    };
}
function refKey(ref) {
    return equationRowKey({ kind: 'value', ref, op: 'exists' });
}
function variableEntry(group, ref, choices, options = {}) {
    const type = options.type ??
        (ref.source === 'system' || ref.source === 'contact'
            ? FIELD_VALUE_TYPES[ref.field]
            : 'text');
    return {
        key: refKey(ref),
        group,
        label: variableName(ref),
        states: [],
        value: {
            ref,
            type,
            choices,
            // A system variable always has a value, so it takes equals only.
            operators: ref.source === 'system' ? ['equals'] : OPERATORS_BY_VALUE_TYPE[type],
            maybeMissing: options.maybeMissing ?? false,
            emptyNote: options.emptyNote,
        },
    };
}
function eventEntry(key, label, row, group = 'Events', description = '') {
    return { key, group, label, description, states: [{ key, label, row }] };
}
function trueFalse() {
    return [
        { value: 'true', label: 'true' },
        { value: 'false', label: 'false' },
    ];
}
function routingChoices(routing, field) {
    if (routing === undefined)
        return { choices: [], note: 'Not available here' };
    if (routing === 'loading')
        return { choices: [], note: 'Loading…' };
    if (routing === 'failed') {
        return { choices: [], note: 'Could not load the list' };
    }
    const choices = field === 'property' ? routing.properties : routing.propertyGroups;
    return {
        choices,
        note: choices.length === 0 ? 'None set up for this company' : undefined,
    };
}
/**
 * The events a node raises that an arrow can wait on by name. A say node's
 * line and a transfer's outcomes are offered only out of that node; an action
 * node's are its results.
 */
function nodeEvents(step) {
    if (step.type === 'say' || step.type === 'transfer' || step.type === 'action')
        return [];
    return eventsFrom(step).map(option => option.name);
}
/** A node's questions and signals the author has written. */
function answerItems(step) {
    if (step.type === 'form') {
        return step.items.flatMap(item => item.kind === 'question' && item.text.trim()
            ? [
                {
                    source: 'question',
                    id: item.id,
                    text: item.text,
                    type: valueTypeOfAnswer(item.answerType),
                    choices: choicesOfAnswer(item.answerType).map(value => ({
                        value,
                        label: value,
                    })),
                },
            ]
            : []);
    }
    if (step.type === 'conversation') {
        return (step.signals ?? []).flatMap(signal => signal.text.trim()
            ? [
                {
                    source: 'signal',
                    id: signal.id,
                    text: signal.text,
                    type: 'boolean',
                    choices: trueFalse(),
                },
            ]
            : []);
    }
    return [];
}
//# sourceMappingURL=equation-options.js.map